import { observer } from "mobx-react-lite";
import { Box, Center, chakra, Flex, Tooltip } from "@chakra-ui/react";

import { isUndefined } from "@noli/core";
import { langStore, translate } from "@noli/business";

import { toast, useOwnToast } from "@/utils/misc";
import { tasksStore } from "@/stores/tasks";
import { themeStore } from "@/stores/theme";
import SubPanelWithScrollBar from "@/components/SubPanel/WithScrollBar";
import { Editor_Words } from "@/utils/lang/editor";
import classNames from "classnames";
import React from "react";

type CapsuleProps = {
  word: string;
  displayWord?: string;
  busyAlias?: string;
  callback: () => Promise<void>;
  focusing?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
};
const Capsule = observer<CapsuleProps>(
  ({ word, displayWord, busyAlias, callback, focusing, disabled, disabledTooltip }) => {
    const t = useOwnToast();
    const lang = langStore.tgt;
    const { selectColor, selectBgColor } = themeStore.styles;

    async function onClick(): Promise<void> {
      // sanity check
      if (!isUndefined(busyAlias) && tasksStore.busyNodes.has(busyAlias)) {
        toast(t, "info", translate(Editor_Words["already-in-queue-message"], lang));
        return;
      }
      // callback
      await callback();
    }

    const focusColor = "#3fc9a8";
    const disabledColor = "#bbb";

    return (
      <Tooltip
        hasArrow
        py="4px"
        px="12px"
        fontSize="12px"
        placement="top"
        offset={[0, 12]}
        isDisabled={!disabled || !disabledTooltip}
        label={disabledTooltip}
      >
        <Box
          px="12px"
          py="4px"
          mr="6px"
          mb="6px"
          bg={selectBgColor}
          color={focusing ? focusColor : selectColor}
          borderRadius="6px"
          fontSize="12px"
          onClick={onClick}
          cursor={disabled ? "not-allowed" : "pointer"}
          _hover={{
            color: focusColor,
          }}
          _disabled={{
            color: disabledColor,
          }}
          disabled={disabled}
          as="button"
        >
          {translate(displayWord ?? word, lang)}
        </Box>
      </Tooltip>
    );
  },
);
type CapsulesPanelProps = {
  innerMT?: string;
  words: readonly string[];
  displayWords?: readonly string[];
  busyAlias?: string;
  callbackFn: (word: string) => () => Promise<void>;
  panelTitle?: string;
  noSeparator?: boolean;
  focusingWord?: string;
  disabledWords?: string[];
  disabledTooltip?: string;
  className?: string;
};
const ICapsulesPanel: React.FC<CapsulesPanelProps> = ({
  innerMT,
  words,
  displayWords,
  busyAlias,
  callbackFn,
  panelTitle,
  noSeparator,
  focusingWord,
  disabledWords,
  disabledTooltip,
  className,
}) => {
  const { titleColor, separatorColor } = themeStore.styles;

  // h ??= 250;
  const mt = 32;
  const textH = 60;
  // const rest = h - 1 - textH - (noSeparator ? 0 : mt);

  return (
    <Center className={classNames(className, "c-capsule-panel")}>
      <Flex w="100%" flexDirection="column" h="100%">
        {panelTitle && !noSeparator ? (
          <Box w="100%" h="0px" mt={`${mt}px`} borderTop="2px" borderColor={separatorColor}></Box>
        ) : null}
        {panelTitle ? (
          <Flex h={`${textH}px`} color={titleColor} fontSize="14px" align="center">
            {panelTitle}
          </Flex>
        ) : null}
        <SubPanelWithScrollBar flex={1} mt={innerMT}>
          <Flex w="100%" wrap="wrap" align="center">
            {words.map((word, i) => (
              <Capsule
                key={word}
                word={word}
                displayWord={displayWords?.[i]}
                busyAlias={busyAlias}
                callback={callbackFn(word)}
                focusing={word === focusingWord}
                disabled={disabledWords?.includes(word)}
                disabledTooltip={disabledTooltip}
              />
            ))}
          </Flex>
        </SubPanelWithScrollBar>
      </Flex>
    </Center>
  );
};

export const CapsulesPanel = chakra(observer(ICapsulesPanel));
